import { JwtFeature } from 'utility/jwt';

// All known route paths in the app. Active routes appear in the menu via
// `menuCategories` below; archived routes are kept here so URLs still resolve
// but don't show up in navigation.
//
// Routes are nested under their menu category (e.g. routes.analysis.productivity
// → '/analysis/productivity') so the URL mirrors the navigation structure.
// Auth/system routes (base, login, signin-oidc, not-available) live at the root
// because they don't belong to any menu category.
const routes = {
  base: '/',
  login: '/login',
  signinOidc: '/signin-oidc',

  // Shown when a feature-gated route is accessed without the required flag.
  notAvailable: '/not-available',

  improvement: {
    improvements: '/improvement/improvements',
  },
  planning: {
    forecast: '/planning/forecast',
    schedule: '/planning/schedule',
  },
  analysis: {
    productivity: '/analysis/productivity',
    time: '/analysis/time',
  },
  monitoring: {
    realtimeProductivity: '/monitoring/realtime-productivity',
  },
  // Admin items are intentionally ungated beyond auth — backend hasn't
  // assigned a JwtFeature yet. Add `feature` to the menu metadata once it does.
  admin: {
    integrations: '/admin/integrations',
    zoneManagement: '/admin/zone-management',
  },

  // Archived — still resolvable by URL, intentionally absent from the menu.
  // Parked under /archived/ so they're visibly separate in the route tree.
  archived: {
    realtime: '/archived/realtime',
    tictac: '/archived/tictac',
    pickingRows: '/archived/picking-rows',
    actions: '/archived/actions',
    productivityGoals: '/archived/productivity-goals',
  },
} as const;

// Per-route metadata for the navigation mega-menu. `feature` is the JwtFeature
// that gates the route — items the user lacks are rendered dimmed (same
// styling as `disabled: true`). Omit `feature` when the route is intentionally
// ungated (e.g. Admin items waiting for a real flag from backend).
interface MenuRoute extends LabelItem {
  feature?: JwtFeature;
  disabled: boolean;
}

interface MenuCategory {
  id: string;
  name: string;
  routes: MenuRoute[];
}

const menuCategories: MenuCategory[] = [
  {
    id: 'improvement',
    name: 'Improvement',
    routes: [
      {
        id: routes.improvement.improvements,
        name: 'Improvements',
        feature: JwtFeature.Improvements,
        disabled: false,
      },
    ],
  },
  {
    id: 'planning',
    name: 'Planning',
    routes: [
      {
        id: routes.planning.forecast,
        name: 'Forecast',
        feature: JwtFeature.Forecast,
        disabled: false,
      },
      // Schedule page is a mockup — keep the link but disable the menu item.
      {
        id: routes.planning.schedule,
        name: 'Schedule',
        feature: JwtFeature.Activity,
        disabled: true,
      },
    ],
  },
  {
    id: 'analysis',
    name: 'Analysis',
    routes: [
      {
        id: routes.analysis.productivity,
        name: 'Productivity',
        feature: JwtFeature.Internal,
        disabled: false,
      },
      {
        id: routes.analysis.time,
        name: 'Time',
        feature: JwtFeature.Activity,
        disabled: false,
      },
    ],
  },
  {
    id: 'monitoring',
    name: 'Monitoring',
    routes: [
      // Realtime productivity is a brand-new mockup — disable the menu item
      // until the real implementation lands.
      {
        id: routes.monitoring.realtimeProductivity,
        name: 'Realtime',
        feature: JwtFeature.Realtime,
        disabled: true,
      },
    ],
  },
  {
    id: 'admin',
    name: 'Admin',
    // No `feature` on Admin items — backend hasn't assigned one yet, so they
    // remain reachable for any authenticated user. Add a JwtFeature here once
    // a real flag exists.
    routes: [
      {
        id: routes.admin.integrations,
        name: 'Integrations',
        disabled: false,
        feature: JwtFeature.Internal,
      },
      {
        id: routes.admin.zoneManagement,
        name: 'Zone Management',
        disabled: false,
        feature: JwtFeature.Internal,
      },
    ],
  },
];

// Drives the breadcrumb display via useRouteName. Derived from menuCategories
// so the menu and the breadcrumb can never drift apart — adding or renaming a
// menu entry automatically updates the breadcrumb mapping.
const routeNames: Record<string, [string, string]> = Object.fromEntries(
  menuCategories.flatMap((cat) =>
    cat.routes.map((r) => [r.id, [cat.name, r.name]] as const),
  ),
);

export type { MenuCategory, MenuRoute };
export { menuCategories, routeNames, routes };
